import type * as Item from "../../lib/models/item.server.ts"
import type * as User from "../../lib/models/user.server.ts"

import * as UI from "@iyk/ui"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { each } from "@iyk/flow"
import { getSingularOrPluralTerm } from "@iyk/localization"
import { toSentenceCase, toTemplate } from "@iyk/string"
import { Link } from "@remix-run/react"
import { useLang } from "../../lib/lang/use-lang.ts"
import { ItemMedia } from "./item-media.tsx"

const PREVIEW_ITEMS_COUNT = 4

export const CommonItemsDialog = (props: CommonItemsDialogProps) => {
  const { currentUser, targetUser, items, usedInFloatingElement } = props
  const lang = useLang()

  if (items.length === 0) {
    return null
  }

  const firstItem = items[0]
  const remainingItemsCount = items.length - 1

  const commonItemsImages = (
    <UI.OverlapGroup overlapInPixels={4} initialZIndex={usedInFloatingElement ? 999 : -1}>
      {items
        ?.slice(0, PREVIEW_ITEMS_COUNT)
        .map((item) => (
          <ItemMedia
            kind="outline"
            key={item.id}
            src={item.imageUrl || UI.defaultAvatarSVG}
            className="size-[18px]"
          />
        ))}
    </UI.OverlapGroup>
  )

  const firstItemLink = (
    <Link className="underline" to={`/creators/${firstItem.team.urlSlug}/items/${firstItem.id}`}>
      {firstItem.name}
    </Link>
  )

  if (remainingItemsCount === 0) {
    return (
      <div className="flex items-center gap-2">
        {commonItemsImages}
        <p className="text-gray-11 text-xs">
          {toSentenceCase(lang.terms.YOU_BOTH_OWN)} {firstItemLink}
        </p>
      </div>
    )
  }
  return (
    <Dialog.Root>
      <div className="flex items-center gap-2">
        {commonItemsImages}
        <p className="text-gray-11 text-xs">
          {toSentenceCase(lang.terms.YOU_BOTH_OWN)} {firstItemLink} {lang.terms.AND}{" "}
          <Dialog.Trigger className="underline">
            {remainingItemsCount} {lang.terms.OTHER}{" "}
            {getSingularOrPluralTerm(
              remainingItemsCount,
              lang.terms.ITEM,
              lang.terms.ITEM__OTHER,
              lang.name,
            )}
          </Dialog.Trigger>
        </p>
      </div>
      <Dialog.Portal>
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} className="px-0 py-4">
          <Dialog.Title hidden />
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2 px-4">
              <UI.OverlapGroup overlapInPixels={8}>
                <UI.Avatar
                  variant="circle"
                  src={currentUser.avatarUrl || UI.defaultAvatarSVG}
                  className="size-6"
                />
                <UI.Avatar
                  variant="circle"
                  src={targetUser.avatarUrl || UI.defaultAvatarSVG}
                  className="size-6"
                />
              </UI.OverlapGroup>
              <span className="text-sm">
                {toSentenceCase(
                  toTemplate(lang.terms.YOU_BOTH_OWN__OTHER, {
                    user: targetUser.username,
                  }),
                )}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <p className="px-4 text-xs-upper">
                {toSentenceCase(lang.terms.ITEM__OTHER)}{" "}
                <span className="text-gray-11">{`(${items.length})`}</span>
              </p>
              <div className="flex flex-col max-h-[70dvh] overflow-y-auto px-2">
                {each(items, (item) => (
                  <Link
                    key={item.id}
                    to={`/creators/${item.team.urlSlug}/items/${item.id}`}
                    className="flex items-center gap-3 py-2.5 px-2 rounded-sm transition-colors hover:bg-gray-2"
                  >
                    <ItemMedia kind="outline" src={item.imageUrl || undefined} className="size-8" />
                    <div className="flex flex-col">
                      <span className="text-xs-upper">{item.team.name}</span>
                      <span className="text-sm">{item.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

type CommonItemsDialogProps = {
  currentUser: User.Profile
  targetUser: User.Profile
  items: Item.ProfilePreview[]
  usedInFloatingElement?: boolean
}

export const CommonItemsDialogSkeleton = () => {
  return (
    <UI.OverlapGroup overlapInPixels={4}>
      {each(Array.from({ length: PREVIEW_ITEMS_COUNT }), (_, index) => (
        <UI.Skeleton key={index} className="size-[18px]" />
      ))}
    </UI.OverlapGroup>
  )
}
